import React from 'react';
import { Link } from 'react-router-dom';

const Investing = () => {
    const services = [
        {
            icon: '📊',
            title: 'Portfolio Strategy',
            description: 'Customized investment strategies aligned with your goals, timeline, and risk tolerance.'
        },
        {
            icon: '🎯',
            title: 'Asset Allocation',
            description: 'Optimal diversification across asset classes to maximize returns and minimize risk.'
        },
        {
            icon: '📈',
            title: 'Market Analysis',
            description: 'In-depth research and insights to identify investment opportunities.'
        },
        {
            icon: '🔄',
            title: 'Rebalancing Strategy',
            description: 'Regular portfolio reviews and rebalancing to maintain optimal allocation.'
        },
        {
            icon: '🎓',
            title: 'Investment Education',
            description: 'Learn investment fundamentals and strategies to make informed decisions.'
        },
        {
            icon: '📋',
            title: 'Financial Planning',
            description: 'Comprehensive financial planning to achieve your long-term wealth goals.'
        }
    ];

    const investmentTypes = [
        { icon: '📈', name: 'Stocks', description: 'Equity investments' },
        { icon: '💵', name: 'Bonds', description: 'Fixed income' },
        { icon: '🏠', name: 'Real Estate', description: 'Property investments' },
        { icon: '📦', name: 'Commodities', description: 'Physical assets' },
        { icon: '💎', name: 'Alternatives', description: 'Private equity & more' }
    ];

    return (
        <div className="page">
            {/* Hero Section — split layout */}
            <section className="page-hero-split">
                <div className="split-content">
                    <h1>Investment <span className="text-gradient">Consulting</span></h1>
                    <p>
                        Strategic investment guidance to help you build and preserve wealth
                        through diversified, well-planned investment strategies.
                    </p>
                </div>
                <div
                    className="split-image"
                    style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/finance-docs.jpg)` }}
                />
            </section>

            {/* Services Section */}
            <section className="section">
                <div className="container">
                    <div className="section-header">
                        <h2>Our <span className="text-gradient">Services</span></h2>
                        <p>Comprehensive investment consulting for every stage of your journey</p>
                    </div>
                    <div className="services-grid">
                        {services.map((service, index) => (
                            <div key={index} className="card card-gradient service-card">
                                <div className="service-icon">{service.icon}</div>
                                <h3>{service.title}</h3>
                                <p>{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Finance divider */}
            <div
                className="image-divider"
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/finance-laptop.jpg)` }}
            >
                <div className="divider-content">
                    <h2>Your Portfolio, <span className="text-gradient">Optimized</span></h2>
                    <p>We bring structure and strategy to your investments across every asset class.</p>
                </div>
            </div>

            {/* Investment Types Section */}
            <section className="section bg-dark">
                <div className="container">
                    <div className="section-header">
                        <h2>Investment <span className="text-gradient">Categories</span></h2>
                        <p>Expertise across all major asset classes</p>
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '1rem'
                    }}>
                        {investmentTypes.map((type, index) => (
                            <div
                                key={index}
                                className="card card-gradient"
                                style={{ textAlign: 'center', padding: '1.5rem' }}
                            >
                                <span style={{ fontSize: '2.5rem' }}>{type.icon}</span>
                                <h4 style={{ margin: '0.5rem 0 0.25rem' }}>{type.name}</h4>
                                <p className="text-muted" style={{ fontSize: '0.875rem' }}>{type.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Investment Principles Section */}
            <section className="section">
                <div className="container">
                    <div className="section-header">
                        <h2>Our Investment <span className="text-gradient">Principles</span></h2>
                        <p>Time-tested strategies for building wealth</p>
                    </div>
                    <div className="features-grid">
                        <div className="feature-item">
                            <div className="feature-icon">🎯</div>
                            <div className="feature-content">
                                <h4>Goal-Based Investing</h4>
                                <p>Every recommendation starts with understanding your unique goals</p>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon">⚖️</div>
                            <div className="feature-content">
                                <h4>Risk Management</h4>
                                <p>Protect your capital while pursuing growth opportunities</p>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon">🔄</div>
                            <div className="feature-content">
                                <h4>Diversification</h4>
                                <p>Spread risk across asset classes and investments</p>
                            </div>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon">⏳</div>
                            <div className="feature-content">
                                <h4>Long-Term Focus</h4>
                                <p>Build wealth through patience and disciplined investing</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section">
                <div className="container">
                    <div className="cta-section">
                        <div className="cta-content">
                            <h2>Ready to Start Investing Smarter?</h2>
                            <p>
                                Schedule a consultation to discuss your investment goals and create a personalized strategy.
                            </p>
                            <Link to="/contact" className="btn btn-primary">
                                Get Investment Advice →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Investing;